import { useEffect, useState } from "react";
import { CircleCheck, Frown } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import { useAuth } from "../../stores/auth";
import { ResendVerificationEmail } from "../../components/ResendVerificationEmail";
import { Button, Spinner } from "../../components/ui";

export function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const user = useAuth((s) => s.user);
  const refreshUser = useAuth((s) => s.refreshUser);
  const [state, setState] = useState<"pending" | "ok" | "error">("pending");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setState("error");
      setMessage("Missing verification token.");
      return;
    }
    api("/auth/verify-email", { body: { token } })
      .then(async () => {
        setState("ok");
        await refreshUser();
      })
      .catch((err) => {
        setState("error");
        setMessage(err instanceof ApiError ? err.message : "Verification failed.");
      });
  }, [token, refreshUser]);

  return (
    <div className="flex min-h-[70vh] flex-col items-center justify-center px-6 text-center">
      {state === "pending" && (
        <>
          <Spinner />
          <p className="mt-3 text-xs text-white/50">Verifying your email…</p>
        </>
      )}
      {state === "ok" && (
        <>
          <CircleCheck size={56} strokeWidth={1.25} className="text-emerald-400" />
          <h1 className="mt-4 font-display text-lg font-bold">Email verified!</h1>
          <p className="mt-2 text-xs text-white/50">You're all set — happy collecting.</p>
          <Link to="/" className="mt-6">
            <Button>Start exploring</Button>
          </Link>
        </>
      )}
      {state === "error" && (
        <>
          <Frown size={56} strokeWidth={1.25} className="text-white/40" />
          <h1 className="mt-4 font-display text-lg font-bold">Verification failed</h1>
          <p className="mt-2 text-xs text-white/50">{message}</p>
          {user && !user.emailVerifiedAt && (
            <div className="mt-6 w-full max-w-sm">
              <ResendVerificationEmail />
            </div>
          )}
          <Link to="/" className="mt-6">
            <Button variant="outline">Back to home</Button>
          </Link>
        </>
      )}
    </div>
  );
}
